<template>
  <div class="rubrica-tabla">
    <!-- DOCUMENTAL · matriz [grado × criterio] -->
    <div v-if="esDocumental" class="tabla-scroll">
      <table class="tabla-rubrica">
        <thead>
          <tr>
            <th class="col-grado">GRADO CUMPLIMIENTO</th>
            <th v-for="c in columnasDocumental" :key="c">{{ c }}</th>
            <th class="col-escala">ESCALA CUANTITATIVA</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(g, i) in gradosDocumental" :key="i" :class="{ 'fila-activa': esGradoActivo(g.rango_min, g.rango_max) }">
            <td class="celda-grado">{{ g.grado }}</td>
            <td v-for="c in columnasDocumental" :key="c">{{ g.descriptores?.[c] || '—' }}</td>
            <td class="celda-escala">{{ formatoRango(g.rango_min, g.rango_max) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- OPINIÓN · tabla de valoración + muestreo + conversión -->
    <div v-else-if="rubrica && esRubricaOpinion(rubrica)">
      <RubricaTablaOpinion v-if="rubrica.grados?.length" :rubrica="rubrica" />

      <div v-if="rubrica.muestreo?.length" class="bloque">
        <h5 class="bloque-titulo">Diseño de muestreo</h5>
        <div class="tabla-scroll">
          <table class="tabla-rubrica">
            <thead>
              <tr>
                <th>GRUPO DE INTERÉS</th>
                <th>TIPO DE MUESTREO</th>
                <th>CRITERIOS</th>
                <th>TÉCNICA</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(m, i) in rubrica.muestreo" :key="i">
                <td class="celda-grado">{{ m.grupo_interes }}</td>
                <td>{{ m.tipo_muestreo }}</td>
                <td>{{ m.criterios || '—' }}</td>
                <td>{{ m.tecnica }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="bloque">
        <h5 class="bloque-titulo">Regla de conversión a 0–5</h5>
        <p class="conversion"><strong>Fórmula:</strong> {{ rubrica.conversion?.formula || '—' }}</p>
        <p class="conversion"><strong>Escala:</strong> {{ rubrica.conversion?.escala || '—' }}</p>
      </div>
    </div>

    <!-- CUANTITATIVA · tendencias -->
    <div v-else-if="rubrica && esRubricaCuantitativa(rubrica)" class="tabla-scroll">
      <table class="tabla-rubrica">
        <thead>
          <tr>
            <th class="col-grado">GRADO CUMPLIMIENTO</th>
            <th>CRITERIO</th>
            <th>DESCRIPCIÓN</th>
            <th class="col-escala">ESCALA CUANTITATIVA</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(n, i) in rubrica.niveles" :key="i" :class="{ 'fila-activa': esGradoActivo(n.rango_min, n.rango_max) }">
            <td class="celda-grado">{{ n.grado || n.label || '—' }}</td>
            <td class="celda-centro">{{ n.tendencia || '—' }}</td>
            <td>{{ n.descripcion || '—' }}</td>
            <td class="celda-escala">{{ formatoRango(n.rango_min, n.rango_max) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <p v-else class="vacio">No hay una rúbrica que mostrar.</p>
  </div>
</template>

<script lang="ts" setup>
/**
 * Tabla de una rúbrica, según su tipo de información. Es la misma que se ve en
 * Gestión de Rúbricas → Ver y la que acompaña a la calificación: se comparte
 * para que el que califica lea exactamente la rúbrica que se le definió.
 *
 * `notaActual` resalta la banda en la que cae la nota que se está poniendo.
 */
import { computed } from 'vue'
import type { RubricaCriterio, RubricaDocumental, GradoDocumental } from '@/interfaces/CriterioEvaluacion'
import {
  esRubricaDocumental,
  esRubricaCuantitativa,
  esRubricaOpinion
} from '@/interfaces/CriterioEvaluacion'
import RubricaTablaOpinion from '@/components/RubricaTablaOpinion.vue'

const props = defineProps<{
  rubrica: RubricaCriterio | null | undefined
  notaActual?: number | null
}>()

const gradosDocumental = computed<GradoDocumental[]>(() => {
  const r = props.rubrica as RubricaDocumental | null | undefined

  return Array.isArray(r?.grados) ? r.grados : []
})

/**
 * Una rúbrica documental es la que reparte descriptores por grado. Se acepta
 * aunque le falte la lista `criterios` —las columnas salen entonces de los
 * propios descriptores— para no dejar en blanco una rúbrica editada a mano.
 */
const esDocumental = computed(() => {
  const r = props.rubrica
  if (!r || esRubricaOpinion(r) || esRubricaCuantitativa(r)) return false

  return esRubricaDocumental(r) || gradosDocumental.value.some(g => !!g.descriptores)
})

const columnasDocumental = computed<string[]>(() => {
  const declaradas = (props.rubrica as RubricaDocumental | null | undefined)?.criterios
  if (Array.isArray(declaradas) && declaradas.length) return declaradas

  // Sin lista declarada, las columnas son las claves de los descriptores.
  const claves = new Set<string>()
  for (const g of gradosDocumental.value) {
    Object.keys(g.descriptores ?? {}).forEach(k => claves.add(k))
  }

  return [...claves]
})

function esGradoActivo(min?: number | null, max?: number | null): boolean {
  const nota = props.notaActual
  if (nota == null || min == null || max == null) return false
  return nota >= min && nota <= max
}

/** "4.5 a 5.0" — se conserva un decimal para que las bandas se lean parejas. */
function formatoRango(min?: number, max?: number): string {
  if (min == null || max == null) return '—'
  return `${formatoNumero(min)} a ${formatoNumero(max)}`
}

function formatoNumero(v: number): string {
  // 5 → "5.0" · 4.49 → "4.49" (no se recorta el segundo decimal si existe)
  return Number.isInteger(v) ? v.toFixed(1) : String(v)
}
</script>

<style scoped>
.tabla-scroll {
  overflow-x: auto;
}

.tabla-rubrica {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78rem;
}

.tabla-rubrica th {
  background: #5b9bd5;
  color: #fff;
  font-weight: 700;
  text-align: center;
  padding: 0.55rem 0.6rem;
  border: 1px solid #b8cce4;
}

.tabla-rubrica td {
  border: 1px solid #b8cce4;
  padding: 0.5rem 0.6rem;
  vertical-align: top;
  color: #222;
  text-align: justify;
}

.tabla-rubrica tbody tr:nth-child(odd) {
  background: #deeaf6;
}

.tabla-rubrica tbody tr:nth-child(even) {
  background: #fff;
}

/* Banda en la que cae la nota que se está poniendo. */
.tabla-rubrica tbody tr.fila-activa,
.tabla-rubrica tbody tr.fila-activa:nth-child(odd) {
  background: #fff4d6;
  box-shadow: inset 3px 0 0 #eea222;
}

.celda-grado {
  font-weight: 700;
  white-space: nowrap;
  text-align: left;
}

.celda-centro {
  text-align: center;
  font-weight: 600;
}

.celda-escala {
  text-align: center;
  white-space: nowrap;
}

.col-grado {
  min-width: 150px;
}

.col-escala {
  min-width: 110px;
}

.bloque {
  margin-top: 1rem;
}

.bloque-titulo {
  margin: 0 0 0.5rem;
  font-size: 0.85rem;
  font-weight: 700;
  color: #14507d;
}

.conversion {
  margin: 0 0 0.3rem;
  font-size: 0.82rem;
  color: #333;
}

.vacio {
  margin: 0;
  color: #888;
  font-size: 0.85rem;
}
</style>
